import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Form as FormUntyped,
  TextField as TextFieldUntyped,
  Button as ButtonUntyped,
  SelectBox as SelectBoxUntyped,
  MultiSelect as MultiSelectUntyped,
} from "../../primary";
import styles from "./assign.module.scss";
import { EDIT, CANCEL_ID, CANCEL_BTN, SUBMIT_ID, SAVE_BTN, SUCCESS_STATUS } from "../../../constants";
import editIcon from "../../../static/images/edit-icon-blue.png";
import rightArrow from "../../../static/images/right_arrow.svg";
import { formDetailsAsync } from "../../../app/customFormSlice";
import { getFacilityLabelByName } from "../../../utils";
import Loader from "../../../static/images/spinner.svg";
import useFacilityOptions from "../../../hooks/useFacilityOptions";
import ViewAssign from "./viewAssign";
import AsyncTagSelect from "../../secondary/AsyncTagSelect/AsyncTagSelect";
import { PERMISSIONS } from "../../../constants/permissions";
import { useFacilitiesByCustomerNumber, useUpdateFormDetailsMutation } from "../../../features/custom-forms";
import { searchAssignEmails } from "../../../features/custom-forms/api";
import type { AssignEmailOption, AssignInfo, FacilityOptionRow, SoldToNumber, UpdateFormDetailsPayload } from "../../../features/custom-forms";
import type { ApiEnvelope } from "../../../types/api";

const Form = FormUntyped as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  onSubmit: (...args: never[]) => unknown;
  requiredPermission?: string;
}>;

const TextField = TextFieldUntyped as unknown as React.ComponentType<{
  id?: string;
  name?: string;
  label?: React.ReactNode;
  classes?: Record<string, string>;
  type?: string;
  note?: React.ReactNode;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  defaultValue?: unknown;
  customError?: string;
  disabled?: boolean;
}>;

const Button = ButtonUntyped as unknown as React.ComponentType<{
  classes?: Record<string, string>;
  type?: string;
  id?: string;
  onClick?: () => void;
  children: React.ReactNode;
}>;

const SelectBox = SelectBoxUntyped as unknown as React.ComponentType<{
  id?: string;
  name?: string;
  label?: React.ReactNode;
  classes?: Record<string, string>;
  optionValue: Array<{ key: string; value: string }>;
  defaultValue?: unknown;
  errors?: unknown;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}>;

const MultiSelect = MultiSelectUntyped as unknown as React.ComponentType<{
  id?: string;
  name?: string;
  label?: React.ReactNode;
  classes?: Record<string, string>;
  optionValue: FacilityOptionRow[];
  validator?: (e: unknown) => void;
  placeholder?: string;
  showCheckbox?: boolean;
  displayValue?: string;
  showSelectAll?: boolean;
  onSelect?: (selected: FacilityOptionRow[]) => void;
  onRemove?: (selected: FacilityOptionRow[]) => void;
  defaultValue?: FacilityOptionRow[];
}>;

export const Assign: React.FC = () => {
  const dispatch = useDispatch();
  const formData = useSelector((state: any) => state.forms.formDetailsData);
  const isFormLoading = useSelector((state: any) => state.forms.isFormLoading);

  const { handleSubmit, errors } = useForm({
    mode: "onBlur",
  });
  const [emailTags, setEmailTags] = useState<AssignEmailOption[]>([]);
  const [editable, setEditable] = useState(false);
  const [errorAssign, setErrorAssign] = useState(false);
  const [customerNumber, setCustomerNumber] = useState<string | undefined>(formData?.assignInfo?.customerNumber);
  const [removeDefaultOrg, setRemoveDefaultOrg] = useState(true);
  const [org, setOrg] = useState<FacilityOptionRow[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [createGroup, setCreateGroup] = useState(false);
  const [customerNumberInp, setCustomerNumberInp] = useState(false);
  const [customerGroup, setCustomerGroup] = useState<string | null | undefined>(formData?.assignInfo?.customerGroup);
  const [sequence, setSequence] = useState(0);
  const [updateStatus, setUpdateStatus] = useState<ApiEnvelope<UpdateFormDetailsPayload> | Record<string, never>>({});

  const organizationRef = useRef<unknown>(null);

  const { data: facilitiesData } = useFacilitiesByCustomerNumber(customerNumber);
  const updateFormDetailsMutation = useUpdateFormDetailsMutation();

  const facilities = facilitiesData?.data?.facilities ?? [];
  const optionData: FacilityOptionRow[] = useFacilityOptions(facilities);

  const defaultFacilities = useCallback(() => {
    const facilities: FacilityOptionRow[] = [];
    formData?.assignInfo?.organization?.soldToNumbers?.forEach((soldTo: SoldToNumber) => {
      const soldToFacility = facilitiesData?.data?.facilities?.find((f) => f.facilityNumber === soldTo.soldToNumber);
      let soldToFacilityLabel: string | undefined, shipToFacilityLabel: string | undefined;
      if (soldToFacility) {
        soldToFacilityLabel = getFacilityLabelByName(soldToFacility);
      }
      const shipToFacilities = facilitiesData?.data?.facilities?.filter((f) => soldTo.shipToNumbers.includes(f.facilityNumber));
      if (shipToFacilities && shipToFacilities.length > 0) {
        shipToFacilities.forEach((shipToFacility) => {
          shipToFacilityLabel = getFacilityLabelByName(shipToFacility);
          facilities.push({ cat: soldToFacilityLabel as string, key: shipToFacilityLabel as string });
        });
      }
    });
    return facilities;
  }, [facilitiesData?.data?.facilities, formData?.assignInfo?.organization?.soldToNumbers]);

  useEffect(() => {
    setOrg(defaultFacilities());
  }, [defaultFacilities]);

  useEffect(() => {
    const assignInfo: AssignInfo | undefined = formData?.assignInfo;

    if (!assignInfo) {
      setCustomerNumber(undefined);
      return;
    }

    const { customerNumber: assignCustomerNumber, customerGroup: assignCustomerGroup, feature, userEmail } = assignInfo;

    if (assignCustomerNumber) {
      setCustomerNumber(assignCustomerNumber);
      setEditable(false);
    } else if (!assignCustomerGroup) {
      setCustomerNumber(undefined);
    }

    if (feature === "Survey" && Array.isArray(userEmail)) {
      const tags = userEmail.map((email) => ({ value: email, label: email })) as unknown as AssignEmailOption[];
      setEmailTags(tags);
    }
  }, [formData?.assignInfo]);

  const handleEdit = useCallback(() => {
    const assignInfo: AssignInfo | undefined = formData?.assignInfo;
    setErrorMsg("");
    setEditable(true);
    setRemoveDefaultOrg(true);
    setOrg(defaultFacilities());

    const nextCustomerNumber = assignInfo?.customerNumber ?? "";
    const nextCustomerGroup = assignInfo?.customerGroup;
    const feature = assignInfo?.feature;
    const userEmail = assignInfo?.userEmail;
    const hasCustomerNumber = Boolean(nextCustomerNumber);
    setCustomerNumber(nextCustomerNumber);

    if (nextCustomerGroup != null) {
      setCreateGroup(true);
      setCustomerNumberInp(false);
    } else if (hasCustomerNumber) {
      setCreateGroup(false);
      setCustomerNumberInp(true);
    } else {
      setCreateGroup(false);
      setCustomerNumberInp(false);
    }

    if (feature === "Survey" && Array.isArray(userEmail)) {
      setEmailTags(
        userEmail.map((email) => ({ value: email, label: email })) as unknown as AssignEmailOption[]
      );
    } else {
      setEmailTags([]);
    }
  }, [defaultFacilities, formData?.assignInfo]);

  const handleCancel = useCallback(() => {
    setEditable(false);
    setErrorAssign(false);
  }, []);

  const onSubmit = () => {
    const emailIDs = Array.isArray(emailTags) ? emailTags.map(({ label }) => label) : [];
    const soldToMap = new Map<string, { soldToNumber: string; shipToNumbers: string[] }>();

    org.forEach((item) => {
      const soldToNumber = item?.facilityNumber;
      if (!soldToNumber) return;

      if (!soldToMap.has(soldToNumber)) {
        soldToMap.set(soldToNumber, {
          soldToNumber,
          shipToNumbers: [],
        });
      }

      const shipToNumber = item?.facilityNumber;
      if (shipToNumber) {
        soldToMap.get(soldToNumber)?.shipToNumbers.push(shipToNumber);
      }
    });

    const soldToNumbers = Array.from(soldToMap.values());

    const isCustomerGroup = customerGroup !== null && customerGroup !== undefined;
    const feature = formData?.assignInfo?.feature;

    const tempUserData = {
      customerGroup,
      customerNumber: isCustomerGroup ? null : customerNumber,
      organization: isCustomerGroup
        ? null
        : {
            primaryGroupNumber: facilitiesData?.data?.facilities?.find((f) => f.accountGroup === "Z002")?.facilityNumber ?? null,
            soldToNumbers,
          },
      feature,
      subFeature: feature === "NE1 WMS" ? formData?.assignInfo?.subFeature : null,
      sequence,
      ...(feature === "Survey" && { userEmail: emailIDs }),
    };

    const result: UpdateFormDetailsPayload = {
      ...formData,
      assignInfo: {
        ...formData?.assignInfo,
        ...tempUserData,
      },
    };

    const sectionMap = Array.isArray(result.sectionInfo)
      ? (result.sectionInfo as Array<{ sectionId: string; name: string }>).reduce<Record<string, string>>((acc, { sectionId, name }) => {
          acc[sectionId] = name;
          return acc;
        }, {})
      : {};

    result.formBuilderInfo = Array.isArray(result.formBuilderInfo)
      ? (result.formBuilderInfo as Array<Record<string, unknown>>).map((item) => ({
          ...item,
          sectionName: sectionMap[item.sectionId as string] ?? null,
        }))
      : [];

    updateFormDetailsMutation.mutate(result, {
      onSuccess: (response) => {
        if (response?.status === SUCCESS_STATUS) {
          setUpdateStatus(response);
          dispatch(formDetailsAsync(result.id));
        }
      },
    });
    setEditable(false);
    setEmailTags([]);
  };

  const onChangeOrg = useCallback((selectedList: FacilityOptionRow[]) => {
    setErrorAssign(false);
    setOrg([...selectedList]);
    setRemoveDefaultOrg(false);
  }, []);

  const getOrganization = useCallback(() => {
    setEmailTags([]);
    const customerNum = (document.getElementById("customerNumber") as HTMLInputElement | null)?.value;
    if (customerNum) {
      setOrg([]);
      setRemoveDefaultOrg(false);
      setCustomerNumber(customerNum);
      setCreateGroup(false);
      setCustomerNumberInp(true);
    }
  }, []);

  const enterCustomerNumber = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    setEmailTags([]);

    if (!value) {
      setOrg([]);
      setRemoveDefaultOrg(false);
      setCustomerNumber(undefined);
      setCreateGroup(false);
      setCustomerNumberInp(false);
      return;
    }

    if (e.key === "Enter") {
      setOrg([]);
      setRemoveDefaultOrg(false);
      setCustomerNumber(value);
      setCreateGroup(false);
      setCustomerNumberInp(true);
    }
  }, []);

  useEffect(() => {
    let fac = "";
    if ((facilitiesData as { errorMessage?: string } | undefined)?.errorMessage === "Invalid Customer Number.") {
      fac = "Customer number doesn't match our record";
    } else if (facilitiesData?.data !== undefined) {
      if (optionData.length === 0) {
        fac = "Organization mapping is missing for the customer";
      }
    }
    setErrorMsg(fac);
  }, [facilitiesData, optionData.length]);

  const getCustomerGroup = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailTags([]);
    if (e.target.value !== "") {
      setCustomerGroup(e.target.value);
      setCreateGroup(true);
      (document.getElementById("customerNumber") as HTMLInputElement | null)!.value = "";
      setErrorAssign(false);
      setRemoveDefaultOrg(false);
    } else {
      setCustomerGroup(null);
      setCreateGroup(false);
    }
  }, []);

  const handleChangeEmail = useCallback((selectedOptions: AssignEmailOption[] | null) => {
    const finalTags = selectedOptions || [];
    setEmailTags(finalTags);

    if (finalTags.length === 0) {
      setCreateGroup(false);
      setCustomerNumberInp(false);
    }
  }, []);

  const onLoadEmail = useCallback((query: string) => {
    if (!query) {
      return Promise.resolve([]);
    }

    setOrg([]);
    setRemoveDefaultOrg(false);
    setCustomerNumber(undefined);
    setCustomerNumberInp(true);
    const customerGroupInput = document.getElementById("customergroup") as HTMLInputElement | null;
    if (customerGroupInput) customerGroupInput.value = "";
    setCustomerGroup(null);
    setCreateGroup(false);
    const customerNumberInput = document.getElementById("customerNumber") as HTMLInputElement | null;
    if (customerNumberInput) customerNumberInput.value = "";

    return searchAssignEmails(query);
  }, []);

  if (isFormLoading) {
    return (
      <div className={`${"loader"} col-12`}>
        <img src={Loader} alt="loading" />
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.tab_content_top}`}>
        <h2 className={`${styles.tab_content_hdg}`}>
          {formData.assignInfo && formData.name}
          {!editable && (
            <span className={`float-sm-right ${styles.tab_edit} handleEdit`} onClick={handleEdit}>
              <img src={editIcon} alt="edit" />
              {EDIT}
            </span>
          )}
        </h2>
      </div>
      {editable && errorAssign && (
        <span className={`${styles.error_message} text-center`}>
          {formData?.assignInfo?.feature === "NE1 WMS"
            ? "Please select customer number and organization"
            : formData?.assignInfo?.feature === "Survey"
            ? "Please select customer group selection or customer number and organization or email"
            : "Please select customer group selection or customer number and organization"}
        </span>
      )}
      <div className={`col-md-12 ${styles.single_product_block}`}>
        <div className="row">
          <div className={`p-0 ${styles.patient_content}`}>
            {editable && (
              <Form className={`${styles.patient_content} onsubmit`} onSubmit={handleSubmit(onSubmit)} requiredPermission={PERMISSIONS.ADMIN_FORM_BUILDER}>
                {formData?.assignInfo?.feature !== "NE1 WMS" && (
                  <>
                    <SelectBox
                      id={"customergroup"}
                      name={"customergroup"}
                      label={`Customer Group Selection:`}
                      classes={{
                        column: `col-12 ${styles.col_spec}`,
                        inputClassName: styles.basic_select,
                        labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                        inputColumn: `col-3 p-0 ${styles.select_wrapper}`,
                      }}
                      optionValue={[
                        { key: "all", value: "All Customers" },
                        { key: "postacute", value: "Post-Acute" },
                        { key: "acute", value: "Acute" },
                      ]}
                      defaultValue={formData.assignInfo && formData.assignInfo.customerGroup}
                      errors={errors}
                      placeholder="Select"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => getCustomerGroup(e)}
                      disabled={customerNumberInp ? true : false}
                    />
                    <div className="offset-4 mb-3">
                      <strong>OR</strong>
                    </div>
                  </>
                )}

                {formData?.assignInfo?.feature === "Survey" && (
                  <>
                    <div className={`col-12 ${styles.col_spec}`}>
                      <label className={`col-3 col-form-label ${styles.label_spec}`}>Email: </label>
                      <div className="col-7 p-0">
                        <AsyncTagSelect
                          tags={emailTags}
                          onLoad={onLoadEmail}
                          handleChange={handleChangeEmail}
                          placeholder="Type to search email..."
                          inputId="emailDomain"
                          noOptionMessage="Entered email is not available"
                          classNamePrefix="tag-select"
                        />
                      </div>
                    </div>

                    <div className="offset-4 mb-3">
                      <strong>OR</strong>
                    </div>
                  </>
                )}
                <TextField
                  id={"customerNumber"}
                  name={"customerNumber"}
                  label={"Customer Number:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    labelClass: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 text-nowrap`,
                    inputClassName: `${styles.basic_input} col-12 customerNum`,
                  }}
                  type="text"
                  note={
                    <span
                      className={`col-3 col-form-label ${styles.form_span} getorg ${createGroup ? styles.disabled : ""}`}
                      onClick={() => getOrganization()}
                    >
                      Search <img src={rightArrow} alt="" className={`${styles.img_left}`} />
                    </span>
                  }
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => enterCustomerNumber(e)}
                  defaultValue={formData?.assignInfo?.customerNumber}
                  customError={createGroup ? "" : errorMsg}
                  disabled={createGroup ? true : false}
                />

                <MultiSelect
                  id={"organization"}
                  name={"organization"}
                  label={"Organization:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 ${styles.label_spec}`,
                    inputColumn: `col-5 p-0`,
                  }}
                  optionValue={optionData}
                  validator={(e: unknown) => {
                    organizationRef.current = e;
                  }}
                  placeholder="Select"
                  showCheckbox={true}
                  displayValue="cat"
                  showSelectAll
                  onSelect={onChangeOrg}
                  onRemove={onChangeOrg}
                  defaultValue={removeDefaultOrg ? defaultFacilities() : Array.isArray(org) ? org : []}
                />

                <SelectBox
                  id={"feature"}
                  name={"feature"}
                  label={`Feature:`}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 ${styles.select_wrapper}`,
                  }}
                  optionValue={[
                    { key: "NE1 WMS", value: "NE1 WMS" },
                    { key: "Product Selector", value: "Product Selector" },
                    { key: "Survey", value: "Survey" },
                  ]}
                  defaultValue={formData?.assignInfo?.feature}
                  disabled={true}
                />
                <SelectBox
                  id={"subFeature"}
                  name={"subFeature"}
                  label={`Sub-Feature:`}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 ${styles.select_wrapper}`,
                  }}
                  optionValue={[
                    { key: "null", value: "Select" },
                    { key: "Characteristics", value: "Characteristics" },
                    { key: "Treatment", value: "Treatment" },
                  ]}
                  defaultValue={formData.assignInfo && formData.assignInfo.subFeature}
                  disabled={true}
                />
                <TextField
                  id={"sequence"}
                  name={"sequence"}
                  label={"Sequence:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    labelClass: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: "col-5 p-0",
                    inputClassName: `${styles.basic_input} `,
                  }}
                  type="number"
                  defaultValue={formData.assignInfo && formData.assignInfo.sequence}
                  disabled={formData?.assignInfo?.feature !== "NE1 WMS" ? true : false}
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => setSequence(Number((e.target as HTMLInputElement).value))}
                />
                <div className="col-sm-12">
                  <div className="text-center pb-4">
                    <br />
                    {/* Cancel button  */}
                    <Button classes={{ className: `btn mr-3 ${styles.btn_custom} ${styles.btn_small}` }} type="submit" id={SUBMIT_ID}>
                      {SAVE_BTN}
                    </Button>
                    <Button
                      classes={{ className: `btn ${styles.btn_custom} ${styles.btn_small_cancel} handleCancel` }}
                      id={CANCEL_ID}
                      onClick={handleCancel}
                    >
                      {CANCEL_BTN}
                    </Button>

                    {/* Save Button */}
                  </div>
                </div>
              </Form>
            )}
            {!editable && (
              <ViewAssign facilitiesData={facilitiesData} updateStatus={updateStatus} onClearUpdateStatus={() => setUpdateStatus({})} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
